import { cn } from "@/lib/utils";
import { Divider } from "components/core/Divider";
import { Floating } from "components/core/Floating";
import React, { ComponentProps } from "react";

import {
    MENU_DEFAULT_PADDING,
    MENU_DEFAULT_SIZE,
    MENU_ITEM_PADDING,
    MenuContext,
    MenuContextProps,
    useMenuContext
} from "./MenuContext";
import { MenuItem } from "./MenuItem";

type MenuProps = ComponentProps<typeof Floating> &
    Partial<MenuContextProps> & {
        style?: React.CSSProperties;
        className?: string;
        autoPadding?: boolean;
        placeholder?: string;
        closeOnClick?: boolean;
    };

function Component({
    style,
    className,
    children,
    fitToAnchor = true,
    autoPadding = true,
    placeholder,
    size = MENU_DEFAULT_SIZE,
    offset = 4,
    ...props
}: MenuProps) {
    return (
        <Floating fitToAnchor={fitToAnchor} offset={offset} {...props}>
            {(context) => (
                <MenuContext.Provider
                    value={{ size, setOpen: context.onOpenChange }}
                >
                    <MenuContainer
                        style={style}
                        className={className}
                        autoPadding={autoPadding}
                    >
                        {/*
                         * Given design use MenuItem component as placeholder, not using CSS.
                         * Instead of insert duplicated placeholder CSS, use MenuItem component as a placeholder.
                         */}
                        {(typeof children === "function"
                            ? children(context)
                            : children) ||
                            (placeholder && (
                                <MenuPlaceholder className="placeholder">
                                    {placeholder}
                                </MenuPlaceholder>
                            ))}
                    </MenuContainer>
                </MenuContext.Provider>
            )}
        </Floating>
    );
}

function MenuContainer({
    autoPadding = true,
    className,
    ...props
}: ComponentProps<"div"> & {
    autoPadding?: boolean;
}) {
    return (
        <div
            className={cn(
                "flex min-w-[160px] flex-col rounded-medium border border-line bg-secondary-contrast shadow-menu",
                autoPadding && "py-2",
                "[&:has(_.placeholder:first-of-type)_.placeholder]:!block [&_.placeholder]:hidden",
                className
            )}
            {...props}
        />
    );
}

function MenuDivider({ className, ...props }: ComponentProps<typeof Divider>) {
    return (
        <div className={cn("py-2", className)} {...props}>
            <Divider />
        </div>
    );
}

function MenuPlaceholder({
    children,
    className,
    ...props
}: ComponentProps<typeof MenuItem>) {
    const { size } = useMenuContext();

    return (
        <MenuItem className={cn("pointer-events-none", className)} {...props}>
            <div
                className="text-text-secondary"
                style={{
                    paddingTop: `${MENU_ITEM_PADDING[size].vertical}px`,
                    paddingBottom: `${MENU_ITEM_PADDING[size].vertical}px`
                }}
            >
                {children}
            </div>
        </MenuItem>
    );
}

const MenuPadding = ({ className, ...props }: ComponentProps<"div">) => {
    return <div className={cn("py-1", className)} {...props} />;
};

const MenuBottomButtonGroup = ({
    children,
    startItem,
    className,
    ...props
}: {
    children?: React.ReactNode;
    startItem?: React.ReactNode;
} & ComponentProps<"div">) => {
    return (
        <div
            className={cn(
                "flex items-center justify-between px-3 py-2",
                className
            )}
            {...props}
        >
            <div>{startItem}</div>
            <div className="flex items-center gap-1">{children}</div>
        </div>
    );
};

const Menu = Object.assign(Component, {
    Container: MenuContainer,
    Divider: MenuDivider,
    Padding: MenuPadding,
    BottomButtonGroup: MenuBottomButtonGroup,
    Placeholder: MenuPlaceholder
});

export { Menu };
